import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { ToastProvider } from './contexts/ToastContext';
import GlobalToastContainer from './components/ToastContainer';

// Public Pages
import Home from './pages/public/Home';
import Login from './pages/auth/Login';
import ForcePasswordChange from './pages/auth/ForcePasswordChange';

// Dashboard Pages
import DashboardLayout from './components/layout/DashboardLayout';
import Dashboard from './pages/dashboard/Dashboard';
import StaffDashboard from './pages/dashboard/StaffDashboard';
import StaffAttendance from './pages/dashboard/StaffAttendance';
import MealRequestManagement from './pages/dashboard/MealRequestManagement';
import SchoolMealCostManagement from './pages/dashboard/SchoolMealCostManagement';
import BranchesManagement from './pages/dashboard/BranchesManagement';
import SchoolsManagement from './pages/dashboard/SchoolsManagement';
import TasksManagement from './pages/dashboard/TasksManagement';
import SettingsManagement from './pages/dashboard/SettingsManagement';
import UsersManagement from './pages/dashboard/UsersManagement';
import RolesManagement from './pages/dashboard/RolesManagement';
import ReportsManagement from './pages/dashboard/ReportsManagement';
import ActivitiesManagement from './pages/dashboard/ActivitiesManagement';
import LeaveManagement from './pages/dashboard/LeaveManagement';
import CalendarManagement from './pages/dashboard/CalendarManagement';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (isAuthenticated && user?.force_password_change && location.pathname !== '/force-change-password') {
    return <Navigate to="/force-change-password" replace />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Page transition wrapper
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

function App() {
  return (
    <AuthProvider>
      <LayoutProvider>
        <ToastProvider>
          <Router>
            <div className="App">
              <AnimatePresence mode="wait">
                <Routes>
                {/* Public Routes */}
                <Route 
                  path="/" 
                  element={
                    <PageTransition>
                      <Home />
                    </PageTransition>
                  } 
                />
                
                {/* Auth Routes */}
                <Route 
                  path="/login" 
                  element={
                    <PageTransition>
                      <Login />
                    </PageTransition>
                  } 
                />

                <Route 
                  path="/force-change-password" 
                  element={
                    <ProtectedRoute>
                      <PageTransition>
                        <ForcePasswordChange />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Protected Dashboard Routes */}
                <Route 
                  path="/dashboard/*" 
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<StaffDashboard />} />
                  <Route path="attendance" element={<StaffAttendance />} />
                  <Route path="leave-management" element={<LeaveManagement />} />
                  <Route path="meal-requests" element={<MealRequestManagement />} />
                  <Route path="school-meal-costs" element={<SchoolMealCostManagement />} />
                  <Route path="branches" element={<BranchesManagement />} />
                  <Route path="schools" element={<SchoolsManagement />} />
                  <Route path="tasks" element={<TasksManagement />} />
                  <Route path="calendar" element={<CalendarManagement />} />
                  <Route path="settings" element={<SettingsManagement />} />
                  <Route path="users" element={<UsersManagement />} />
                  <Route path="roles" element={<RolesManagement />} />
                  <Route path="reports" element={<ReportsManagement />} />
                  <Route path="activities" element={<ActivitiesManagement />} />
                  <Route path="old" element={<Dashboard />} />
                  {/* Add more dashboard routes here */}
                </Route>
                
                {/* Catch all - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AnimatePresence>
            
            <GlobalToastContainer />
          </div>
        </Router>
      </ToastProvider>
    </LayoutProvider>
  </AuthProvider>
  );
}

export default App;
